import { useCallback, useMemo } from 'react';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useAcquisitionAwareUtm } from '@shared/hooks/useAcquisitionAwareUtm';
import { useHotelRoutePaths } from '@shared/utils/accommodations/hotelRoutes';
import type { AccommodationListingDTO, AccommodationsConsoleState } from '@shared/types/Accommodations.types';
import { RedirectMechanismType } from '@graphql/generated-base';
import { extractHotelRedirects } from '@shared/utils/extractHotelRedirects';
import { useTravelWhiteLabelCurrency } from '@shared/hooks/useTravelWhiteLabelCurrency';
import { convertToJoyNativeCheckoutUrl, formatRoomOccupancyToString, JOY_HOTEL_DISCOUNT_CODE_PARAM } from '@shared/utils/joyHotelUrl';
import { isValidBookingUrl } from '@shared/utils/accommodations';
import { useQueryParams } from '@shared/utils/hooks/useQueryParams';
import type { HotelSearchSource } from '@shared/constants/accommodations';
import { useFormatRedirectURL } from '@shared/utils/formatting/redirectURL.formatter';

/**
 * Search parameters for accommodation redirect URLs
 */
type AccommodationSearchParams = Readonly<{
  rooms?: string;
  checkIn?: string;
  checkOut?: string;
  utm_source: string;
  utm_campaign: string;
  skipIfNoSearchParams?: string;
  utm_medium: 'web' | 'email' | 'text';
  joyHotelDiscountCode?: string;
  skipAvailabilityCheck?: string;
}>;

export const createUtmParametersForAccommodationsConsolePage = (sourceName: 'guest_map' | 'admin_map' | 'ota'): AccommodationSearchParams => {
  return { utm_medium: 'web', utm_campaign: sourceName, utm_source: 'hotel_search' };
};

export const useAccommodationRedirectUrl = (state: AccommodationsConsoleState, sourceName: HotelSearchSource) => {
  const { eventHandle } = useEventInfo();
  const hotelRoute = useHotelRoutePaths();
  const { currency } = useTravelWhiteLabelCurrency();
  const formatRedirectURL = useFormatRedirectURL();
  const queryParams = useQueryParams();
  const rawDiscountCode = queryParams[JOY_HOTEL_DISCOUNT_CODE_PARAM];
  const discountCode = Array.isArray(rawDiscountCode) ? rawDiscountCode[0] : rawDiscountCode;

  // Native checkout is built outside `buildRelativeUrl`, so resolve its UTM with the same shared hook.
  const internalUtm = useMemo(() => createUtmParametersForAccommodationsConsolePage(sourceName), [sourceName]);
  const resolvedUtm = useAcquisitionAwareUtm(internalUtm);
  const checkoutUtmParams = useMemo(
    () => ({
      utm_medium: resolvedUtm.utm_medium ?? internalUtm.utm_medium,
      utm_source: resolvedUtm.utm_source ?? internalUtm.utm_source,
      utm_campaign: resolvedUtm.utm_campaign ?? internalUtm.utm_campaign,
      ...(resolvedUtm.utm_source_secondary && { utm_source_secondary: resolvedUtm.utm_source_secondary }),
    }),
    [resolvedUtm, internalUtm],
  );

  return useCallback(
    (listing: AccommodationListingDTO, options?: { skipAvailabilityCheck?: boolean }) => {
      if (!eventHandle) {
        return undefined;
      }

      if (listing.redirectMechanisms) {
        const { hotelSearchPageUrl, hotelPageUrl } = extractHotelRedirects(listing.redirectMechanisms);
        const redirectData = hotelSearchPageUrl || hotelPageUrl;

        if (listing.isSponsored && redirectData?.type === RedirectMechanismType.custom) {
          return formatRedirectURL({ url: redirectData.url.toString() });
        }

        if (redirectData?.type === RedirectMechanismType.joyNativeCheckout) {
          return convertToJoyNativeCheckoutUrl({
            currency,
            discountCode,
            url: redirectData.url,
            checkIn: state.searchDates?.checkin,
            checkOut: state.searchDates?.checkout,
            roomsString: state.occupancies?.occupancies?.map(formatRoomOccupancyToString).join(','),
            utmParams: checkoutUtmParams,
          }).toString();
        }
      }

      const paramsData: AccommodationSearchParams = {
        // Raw internal defaults are fine here — `buildRelativeUrl` applies the inbound-wins merge.
        ...internalUtm,
        // Treatment: skip the sold-out interceptor on the redirect page and go straight to the hotel PDP.
        ...(options?.skipAvailabilityCheck && { skipAvailabilityCheck: 'true' }),
        ...(discountCode && { joyHotelDiscountCode: discountCode }),
        ...(state.searchDates && {
          checkIn: state.searchDates.checkin,
          checkOut: state.searchDates.checkout,
        }),
        ...(state.occupancies?.occupancies?.length && {
          rooms: state.occupancies.occupancies.map(formatRoomOccupancyToString).join(','),
        }),
        ...(state.searchDates &&
          state.occupancies?.occupancies?.length && {
            skipIfNoSearchParams: 'true',
          }),
      };

      if (listing.type === 'roomBlock') {
        if (!listing.id || !listing.bookingUrl) {
          return undefined;
        }
        if (!isValidBookingUrl(listing.bookingUrl)) {
          return undefined;
        }
        return hotelRoute.roomblock.buildRelativeUrl(eventHandle, listing.id, paramsData);
      }

      if (listing.isPickedByCouple) {
        if (!listing.id) {
          return undefined;
        }
        return hotelRoute.accommodationPlace.buildRelativeUrl(eventHandle, listing.id, paramsData);
      }

      if (!listing.joyPlaceId) {
        return undefined;
      }

      return hotelRoute.hotelDetails.buildRelativeUrl(eventHandle, listing.joyPlaceId, paramsData);
    },
    [
      eventHandle,
      internalUtm,
      checkoutUtmParams,
      discountCode,
      state.searchDates,
      state.occupancies.occupancies,
      hotelRoute.hotelDetails,
      hotelRoute.roomblock,
      hotelRoute.accommodationPlace,
      currency,
      formatRedirectURL,
    ],
  );
};
